import { Contact, ContactDTO } from '@core/models/admin/contacts.types';
import { CommentSentiment } from '@core/models/ApiFields/ig-field-values.types';
import { AIAgent } from '@features/ai-agents/ai-agent.types';
import dayjs, { Dayjs } from 'dayjs';
import { MessageDTO, MessageModel } from '../inb-convos/messages/types/messages.types';
import { Post, PostDTO } from './post.model';

/**
 * There's only one model we recieve from backend,
 * but we don't necesarily need the same fields in all sections of the app
 */
export type SocialCommentDTO = SocialCommentBaseDTO & SocialCommentAnalyticsDTO;

type SocialCommentBaseDTO = {
    comment_id: string;
    comments_negativity?: number;
    comments_positivity?: number;
    contact?: ContactDTO;
    deleted?: boolean;
    department_id?: number;
    from_me: boolean;
    id: number;
    labels?: string[];
    likes?: number;
    negative_comments?: number;
    negative_replies?: number;
    /**
     * This will be a parent comment: the comment that owns the replies.
     * It can never be a reply itself
     * We only support 1 level of inheritance (comment -> n replies)
     */
    parent_comment?: SocialCommentBaseDTO;
    parent_social_comment_id?: number;
    positive_comments?: number;
    positive_replies?: number;
    publish_date: string; // YYYY-MM-DD HH:mm:ss
    read: boolean;
    sentiment: CommentSentiment | undefined;
    social_media_id: number;
    text: string;
    tmp_id?: string;
    total_reactions?: number;
    ts: number; // timestamp
    user_id: number;
    /** User name by channel for backend use */
    username?: string;
    /** IG account name */
    ig_account?: string;
    /** Amount of unread replies to which current filters apply */
    replies_filtered_unread?: number;
    /** Amount of replies to which current filters apply */
    replies_filtered?: number;
    /** Amount of unread public comment replies */
    replies_unread?: number;
    /** Amount of public comment replies */
    replies?: number;
    /** Handled by user (via public reply or DM) */
    handled: boolean;
    /** If handled by user via DM reply  */
    message?: MessageDTO;
    /** If handled by user via public comment reply */
    public_handled?: boolean;
    /** Amount of replies handled by user */
    replies_handled?: number;
    /** Amount of replies handled by user via public comment reply */
    replies_public_handled?: number;
    /** Amount of replies handled by user via DM reply */
    replies_dm_handled?: number;
    ai_agent_id?: AIAgent['id'];
};

export type SocialCommentExtendedDTO = SocialCommentBaseDTO & {
    /**
     * Since we only support 1 level of inheritance (comment -> n replies)
     * we need to know if this reply was in response to another reply\
     * If it was a direct reply to the parent comment, this attribute and parent_comment.id will be the same\
     * If it was a response to a sibling reply, this will be the sibling's ID
     */
    real_parent_social_comment_id?: number;
    social_media: PostDTO;
    /** If true we need to request totals */
    social_media_read_modified: boolean;
};

type SocialCommentAnalyticsDTO = {
    media_id?: string;
    date?: string;
    comments_count?: number;
    month?: string;
    week?: string;
    weekday?: string;
    total_likes?: number;
    total_replies?: number;
};

/** For comment display sections, mainly Inbox */
export class SocialComment implements SocialCommentBaseDTO {
    comment_id: string;
    comments_negativity?: number;
    comments_positivity?: number;
    contact?: Contact;
    deleted?: boolean;
    department_id?: number;
    dm_handled: boolean;
    from_me: boolean;
    handled: boolean;
    id: number;
    ig_account?: string;
    labels?: string[];
    likes?: number;
    message?: MessageModel;
    negative_comments?: number;
    negative_replies?: number;
    parent_comment?: SocialCommentBaseDTO;
    parent_social_comment_id?: number;
    positive_comments?: number;
    positive_replies?: number;
    public_handled?: boolean;
    publish_date: string; // YYYY-MM-DD HH:mm:ss
    read: boolean;
    replies_dm_handled?: number;
    replies_filtered_unread?: number;
    replies_filtered?: number;
    replies_handled?: number;
    replies_public_handled?: number;
    replies_unread?: number;
    replies?: number;
    sentiment: CommentSentiment | undefined;
    social_media_id: number;
    text: string;
    tmp_id?: string;
    total_reactions?: number;
    ts: number; // timestamp
    user_id: number;
    username: string | undefined;
    ai_agent_id?: AIAgent['id'];

    /** Formatted text. Ex: "2 hs ago", "just now", ... */
    dateFromNow: string;
    positiveRatio?: number;
    negativeRatio?: number;
    isReplyViaDmDisabled = false;
    replyDmExpiryDate: Dayjs; // UTC

    constructor({
        comment_id,
        comments_negativity,
        comments_positivity,
        contact,
        deleted,
        department_id,
        from_me,
        handled,
        id,
        ig_account,
        labels,
        likes,
        message,
        negative_comments,
        negative_replies,
        parent_comment,
        parent_social_comment_id,
        positive_comments,
        positive_replies,
        public_handled,
        publish_date,
        read,
        replies_dm_handled,
        replies_filtered_unread,
        replies_filtered,
        replies_handled,
        replies_public_handled,
        replies_unread,
        replies,
        sentiment,
        social_media_id,
        text,
        tmp_id,
        total_reactions,
        ts,
        user_id,
        username,
        ai_agent_id,
    }: SocialCommentDTO) {
        this.comment_id = comment_id;
        this.comments_negativity = comments_negativity;
        this.comments_positivity = comments_positivity;
        this.contact = contact && new Contact(contact);
        this.deleted = deleted;
        this.department_id = department_id;
        this.from_me = from_me;
        this.handled = handled;
        this.id = id;
        this.ig_account = ig_account;
        this.labels = labels;
        this.likes = likes;
        this.message = message && new MessageModel(message);
        this.negative_comments = negative_comments ?? 0;
        this.negative_replies = negative_replies;
        this.parent_comment = parent_comment;
        this.parent_social_comment_id = parent_social_comment_id;
        this.positive_comments = positive_comments ?? 0;
        this.positive_replies = positive_replies;
        this.public_handled = public_handled;
        this.publish_date = publish_date;
        this.read = read;
        this.replies_dm_handled = replies_dm_handled;
        this.replies_filtered_unread = replies_filtered_unread;
        this.replies_filtered = replies_filtered;
        this.replies_handled = replies_handled;
        this.replies_public_handled = replies_public_handled;
        this.replies_unread = replies_unread;
        this.replies = replies;
        this.sentiment = sentiment;
        this.social_media_id = social_media_id;
        this.text = text;
        this.tmp_id = tmp_id;
        this.total_reactions = total_reactions;
        this.ts = ts;
        this.user_id = user_id;
        this.username = username;

        this.ts = ts * 1000;
        this.dm_handled = !!this.message;
        this.dateFromNow = dayjs(publish_date).fromNow();
        this.positiveRatio =
            this.positive_comments / (this.positive_comments + this.negative_comments);
        this.negativeRatio =
            this.negative_comments / (this.positive_comments + this.negative_comments);
        /* can't reply via DM after 7 days since post was created */
        this.replyDmExpiryDate = dayjs(this.ts).add(7, 'day');
        this.isReplyViaDmDisabled = !!this.dm_handled || this.replyDmExpiryDate.diff() < 0;
        this.ai_agent_id = ai_agent_id;
    }
}

export class SocialCommentExtended extends SocialComment implements SocialCommentExtendedDTO {
    real_parent_social_comment_id?: number;
    social_media_read_modified: boolean;
    social_media: Post;

    constructor(dto: SocialCommentExtendedDTO) {
        super(dto);
        this.social_media = new Post(dto.social_media);
        this.real_parent_social_comment_id = dto.real_parent_social_comment_id;
        this.social_media_read_modified = dto.social_media_read_modified;
    }
}

/** For Social Analytics section */
export class SocialCommentAnalytics extends SocialComment implements SocialCommentDTO {
    media_id?: string;
    date?: string;
    comments_count?: number;
    month?: string;
    week?: string;
    weekday?: string;
    total_likes?: number;
    total_replies?: number;

    constructor(commentDTO: SocialCommentDTO) {
        super(commentDTO);
        this.media_id = commentDTO.media_id;
        this.date = commentDTO.date;
        this.comments_count = commentDTO.comments_count;
        this.month = commentDTO.month;
        this.week = commentDTO.week;
        this.weekday = commentDTO.weekday;
        this.total_likes = commentDTO.total_likes;
        this.total_replies = commentDTO.total_replies;
    }
}

/** For optimistic update on comment send */
export type OptimisticSocialCommentPayload = {
    parent_social_comment_id?: Post['id'] | SocialComment['id'];
    text: string;
    user_id: number;
};

export class OptimisticSocialComment extends SocialComment {
    override tmp_id: string;
    constructor({ parent_social_comment_id, text, user_id }: OptimisticSocialCommentPayload) {
        const tmp_id = crypto.randomUUID();
        super({
            id: NaN,
            parent_social_comment_id,
            text,
            publish_date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            ts: dayjs().valueOf(),
            likes: 0,
            replies: 0,
            sentiment: undefined,
            from_me: true,
            handled: false,
            read: true,
            user_id,
            tmp_id,

            comment_id: '',
            social_media_id: NaN,
        });
        this.tmp_id = tmp_id;
    }
}
